import { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";

//imported
import { PAGE_LIMIT, AUDIT_LOGS } from "common/viewContent";
import { CONFIRM_ALERT } from "common/constantMessage";
import * as actions from "store/actions";
import * as Path from "routes/paths";
import { DATE_FORMAT, formatText, userFullName } from "helpers/common";
import ReactPagination from "components/shared/ReactPagination";
import SortableTable from "components/shared/SortableTable";
import { generatePath } from "react-router-dom";
import { getAuditLogsList } from "store/services/auditLogsService";
import { Button, Form } from "react-bootstrap";
import ReportDateRange from "../dashboard/Component/ReportDateRange";
import DiffViewer from "components/shared/DiffViewer";
import ViewDiffPopup from "./Components/ViewDiffPopup";
import { debounce, isEmpty } from "lodash";
import ViewMetadataPopup from "./Components/ViewMetadataPopup";

const List = ({
  dispatch,
  isLoading
}) => {
  //pagination
  const [activePage, setActivePage] = useState(1);
  const [logsList, setLogsList] = useState(null);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [searchFilter, setsearchFilter] = useState({
    order: "desc",
    orderBy: "created_at",
  });

  //get
  async function getData() {
    try {
      let query = {
        page: activePage,
        limit: PAGE_LIMIT,
        ...searchFilter
      };
      dispatch(actions.persist_store({ loader: true }));
      const response = await getAuditLogsList(query);
      if (response?.success && response?.body?.data?.length) {
        setLogsList(response?.body?.data);
        setTotalItemsCount(response?.body?.total_count || 0);
      } else {
        setLogsList(null);
        setTotalItemsCount(0);
      }
      dispatch(actions.persist_store({ loader: false }));
    } catch (err) {
      dispatch(actions.persist_store({ loader: false }));
      console.log(err);
    }
  }
  useEffect(() => {
    getData();
  }, [activePage, searchFilter]);

  //pagination
  const handlePageChange = (page) => {
    setActivePage(page);
    window.scrollTo(0, 0);
  };

  const handleSearchFilter = (e) => {
    let { name, value } = e.target;

    setsearchFilter({ ...searchFilter, [name]: value });

    if (name === "panel" && value === "all-panels") {
    setsearchFilter((prev) => {
      const { panel, ...rest } = prev;
      return rest;
    });
  } 

    if (name === "dateRangeType" && value === "all") {
      setsearchFilter((prev) => ({
        ...prev,
        startDate: null,
        endDate: null,
      }))
    }
  };

  const handleSort = (sortBy, sortOrder) => {
    setsearchFilter((prev) => ({
      ...prev,
      orderBy: sortBy,
      order: sortOrder
    }))
  }

  const handleDebounceSearch = useCallback(debounce((e) => {
    setsearchFilter((prev) => ({
      ...prev,
      eventType: e.target.value,
    }))
  }, 500), []);

  const columns = useMemo(() => {
    return [
      {
        key: "Log id",
        render: (item) => item?.uuid,
      },
      {
        key: "event_type",
        sortable: true,
        render: (item) => formatText(item?.event_type),
      },
      {
        key: "panel",
        sortable: true,
        render: (item) => formatText(item?.panel),
      },
      {
        key: "User Detail",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          const userInfo = metadata?.userInfo;
          if (!userInfo) {
            return "N/A";
          }

          return (
            <div>
              <p className="m-0"><strong>ID: </strong>{userInfo.id}</p>
              <p className="m-0"><strong>Name: </strong>{userFullName(userInfo)}</p>
              <p className="m-0"><strong>Email: </strong>{userInfo.email}</p>
            </div>
          )
        },
      },
      {
        key: "event_source",
        sortable: true,
        render: (item) => formatText(item?.event_source),
      },
      {
        key: "action_at",
        label: "Event Date & Time",
        sortable: true,
        render: (item) => DATE_FORMAT(item.action_at, "MMM D, YYYY H:mm A"),
      },
      {
        key: "Changes",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          if (!metadata?.diff) {
            return "N/A";
          }

          return (
            <ViewDiffPopup
              diffData={metadata}
            />
          )
        }
      },
      {
        key: "Log Metadata",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          if (isEmpty(metadata)) {
            return "N/A";
          }

          return (
            <ViewMetadataPopup
              diffData={metadata}
            />
          )
        }
      },
    ]
  }, [])


  return (
    <>
      <Helmet title={AUDIT_LOGS.CURRENT_MODULE} />
      <section className="section">
        <div className="section-header">
          <h1>{AUDIT_LOGS.CURRENT_MODULE}</h1>
          {/* <div className="section-header-button">
            <Link to={Path.storage_plan_add} className="btn btn-primary">
              Add New
            </Link>
          </div> */}
          <div className="section-header-breadcrumb">
            <div className="breadcrumb-item">View Audit Logs</div>
          </div>
        </div>
        <div className="section-body">
          <h2 className="section-title">{AUDIT_LOGS.CURRENT_MODULE}</h2>
          <p className="section-lead">
            In this section, you can view and track all system activities and user actions for auditing purposes. Use the filters to narrow down specific events.
          </p>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4>{AUDIT_LOGS.TOP_CONTENT}</h4>
                </div>
                <div className="card-body">
                  <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                    <div className="left d-flex align-items-end gap-10">
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Event Type:
                        </label>
                        <div className="searchForm position-relative iconWithText">
                          <input
                            type="text"
                            placeholder="Search...."
                            className="form-control"
                            name="eventType"
                            onChange={handleDebounceSearch}
                          />
                          <Button
                            className="border-0 p-0 position-absolute icn"
                            variant="transparent"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="21"
                              height="21"
                              viewBox="0 0 21 21"
                              fill="none"
                            >
                              <path
                                d="M16.031 14.617L20.314 18.899L18.899 20.314L14.617 16.031C13.0237 17.3082 11.042 18.0029 9 18C4.032 18 0 13.968 0 9C0 4.032 4.032 0 9 0C13.968 0 18 4.032 18 9C18.0029 11.042 17.3082 13.0237 16.031 14.617ZM14.025 13.875C15.2941 12.5699 16.0029 10.8204 16 9C16 5.133 12.867 2 9 2C5.133 2 2 5.133 2 9C2 12.867 5.133 16 9 16C10.8204 16.0029 12.5699 15.2941 13.875 14.025L14.025 13.875Z"
                                fill="#08415C"
                              />
                            </svg>
                          </Button>
                        </div>
                      </div>
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Panel:
                        </label>
                        <Form.Select
                          aria-label="Default select example"
                          name="panel"
                          className="form-control"
                          onChange={handleSearchFilter}
                        >
                          <option value="all-panels">All</option>
                          <option value="practitioner">Practitioner</option>
                          <option value="client">Client</option>
                          <option value="admin">Admin</option>
                          <option value="chat">Chat</option>
                        </Form.Select>
                      </div>
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Event Date Range:
                        </label>
                        <Form.Select
                          aria-label="Default select example"
                          name="dateRangeType"
                          className="form-control"
                          value={searchFilter?.dateRangeType}
                          onChange={handleSearchFilter}
                        >
                          <option value="all">All Time</option>
                          <option value="custom">Custom</option>
                        </Form.Select>
                      </div>
                      {searchFilter?.dateRangeType === "custom" && (
                        <div>
                          <ReportDateRange
                            start={searchFilter?.startDate}
                            end={searchFilter?.endDate}
                            buttonClassName={"p-2 border"}
                            handleSelectDateRange={(start, end) => {
                              setsearchFilter((prev) => ({
                                ...prev,
                                startDate: start,
                                endDate: end,
                              }))
                            }}
                          // handleChangeType={handleChangeType}
                          />
                        </div>
                      )}
                    </div>
                    <div className="right d-flex align-items-center gap-10">
                    </div>
                  </div>

                  <div className="table-responsive">
                    <SortableTable
                      columns={columns}
                      data={logsList}
                      isLoading={isLoading}
                      onSort={handleSort}
                      sortColumn={searchFilter.orderBy}
                      sortOrder={searchFilter.order}
                    />
                  </div>
                  {logsList?.length > 0 && (
                    <ReactPagination
                      activePage={activePage}
                      itemsCountPerPage={PAGE_LIMIT}
                      totalItemsCount={totalItemsCount}
                      handlePageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    subscriptionList: state.Subscription.subscriptions,
    isLoading: state.persistStore.loader,
  };
};

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(mapStateToProps, mapDispatchToProps)(List);
